import React, { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import PostTripConnectContext from './PostTripConnectContext';
import { enquiryService } from '../../services/postTripConnect/enquiryService';
import { packageService } from '../../services/postTripConnect/packageService';
import { notificationPrefService } from '../../services/postTripConnect/notificationPrefService';

export const PostTripConnectState = ({ children }) => {
  // Enquiries State
  const [enquiries, setEnquiries] = useState([]);
  const [enquiriesLoading, setEnquiriesLoading] = useState(false);

  // Packages State
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);

  // Notifications State
  const [preferences, setPreferences] = useState({
    welcome_back: { enabled: true },
    feedback_nudge: { enabled: true },
    new_package_published: { enabled: true },
    seasonal_greeting: { enabled: false },
    trip_anniversary: { enabled: false }
  });
  const [history, setHistory] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // Enquiry Actions
  const fetchEnquiries = useCallback(async (params = {}) => {
    try {
      setEnquiriesLoading(true);
      const res = await enquiryService.getEnquiries(params);
      if (res.success) {
        setEnquiries(res.data);
      }
      return res.data;
    } catch (error) {
      console.error('Failed to fetch enquiries:', error);
      toast.error('Failed to load enquiries');
      return [];
    } finally {
      setEnquiriesLoading(false);
    }
  }, []);

  const updateEnquiryStatus = useCallback(async (id, status) => {
    try {
      await enquiryService.updateStatus(id, status);
      setEnquiries((prev) => prev.map((e) => (e._id === id ? { ...e, status } : e)));
      toast.success('Status updated');
    } catch (error) {
      console.error('Failed to update enquiry status:', error);
      toast.error('Failed to update status');
      throw error;
    }
  }, []);

  const deleteEnquiry = useCallback(async (id) => {
    try {
      await enquiryService.deleteEnquiry(id);
      setEnquiries((prev) => prev.filter((e) => e._id !== id));
      toast.success('Enquiry deleted');
    } catch (error) {
      console.error('Failed to delete enquiry:', error);
      toast.error('Failed to delete enquiry');
      throw error;
    }
  }, []);

  const exportEnquiriesCSV = useCallback(async (params = {}) => {
    try {
      return await enquiryService.exportCSV(params);
    } catch (error) {
      console.error('Failed to export enquiries CSV:', error);
      toast.error('Failed to export CSV');
      throw error;
    }
  }, []);

  // Package Actions
  const fetchPackages = useCallback(async (params = {}) => {
    try {
      setPackagesLoading(true);
      const res = await packageService.getPackages(params);
      if (res.success) {
        setPackages(res.data);
      }
      return res.data;
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error('Failed to load packages');
      return [];
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  const createPackage = useCallback(async (payload) => {
    try {
      const res = await packageService.createPackage(payload);
      toast.success('Package created successfully');
      fetchPackages();
      return res;
    } catch (error) {
      console.error('Failed to create package:', error);
      toast.error(error.response?.data?.message || 'Failed to create package');
      throw error;
    }
  }, [fetchPackages]);

  const updatePackage = useCallback(async (id, payload) => {
    try {
      const res = await packageService.updatePackage(id, payload);
      toast.success('Package updated successfully');
      fetchPackages();
      return res;
    } catch (error) {
      console.error('Failed to update package:', error);
      toast.error(error.response?.data?.message || 'Failed to update package');
      throw error;
    }
  }, [fetchPackages]);

  const deletePackage = useCallback(async (id) => {
    try {
      await packageService.deletePackage(id);
      setPackages((prev) => prev.filter((p) => p._id !== id));
      toast.success('Package deleted');
    } catch (error) {
      console.error('Failed to delete package:', error);
      toast.error('Failed to delete package');
      throw error;
    }
  }, []);

  const togglePackageStatus = useCallback(async (id, status) => {
    try {
      await packageService.toggleStatus(id, status);
      setPackages((prev) => prev.map((p) => (p._id === id ? { ...p, status } : p)));
      toast.success(status === 'published' ? 'Package published' : 'Package unpublished');
    } catch (error) {
      console.error('Failed to update package status:', error);
      toast.error('Failed to update status');
      throw error;
    }
  }, []);

  // Notification Actions
  const fetchNotificationPreferences = useCallback(async () => {
    try {
      setNotificationsLoading(true);
      const res = await notificationPrefService.getPreferences();
      if (res.success) {
        setPreferences(res.data);
      }
      return res.data;
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
      return null;
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  const updateNotificationPreferences = useCallback(async (data) => {
    try {
      const res = await notificationPrefService.updatePreferences(data);
      if (res.success) {
        setPreferences(res.data);
      }
      toast.success('Preferences saved');
      return res;
    } catch (error) {
      console.error('Failed to update preferences:', error);
      toast.error('Failed to save preferences');
      throw error;
    }
  }, []);

  const fetchNotificationHistory = useCallback(async () => {
    try {
      const res = await notificationPrefService.getHistory();
      if (res.success) {
        setHistory(res.data);
      }
      return res.data;
    } catch (error) {
      console.error('Failed to fetch notification history:', error);
      return [];
    }
  }, []);

  const sendCustomBroadcast = useCallback(async (title, message, target) => {
    try {
      const res = await notificationPrefService.sendCustomNotification(title, message, target);
      toast.success('Broadcast notification sent!');
      fetchNotificationHistory();
      return res;
    } catch (error) {
      console.error('Failed to send broadcast:', error);
      toast.error(error.response?.data?.message || 'Failed to send broadcast');
      throw error;
    }
  }, [fetchNotificationHistory]);

  const value = {
    // Enquiries
    enquiries,
    setEnquiries,
    enquiriesLoading,
    fetchEnquiries,
    updateEnquiryStatus,
    deleteEnquiry,
    exportEnquiriesCSV,

    // Packages
    packages,
    setPackages,
    packagesLoading,
    fetchPackages,
    createPackage,
    updatePackage,
    deletePackage,
    togglePackageStatus,

    // Notifications
    preferences,
    setPreferences,
    history,
    setHistory,
    notificationsLoading,
    fetchNotificationPreferences,
    updateNotificationPreferences,
    fetchNotificationHistory,
    sendCustomBroadcast
  };

  return (
    <PostTripConnectContext.Provider value={value}>
      {children}
    </PostTripConnectContext.Provider>
  );
};

export default PostTripConnectState;
